import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { CatalogView } from '@lemoncloud/codes-goods-api';
import { MediaView } from '@lemoncloud/codes-goods-api/dist/modules/medias/views';
import { AlertCircle, RefreshCw } from 'lucide-react';

import { useFetchProductById } from '@codes/product';
import { ImageItem, PdfItem, getCurrencySymbol } from '@codes/react-utils';
import { PdfViewDialog } from '@codes/shared';
import {
    Avatar,
    AvatarImage,
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    Skeleton,
} from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

import { useProductDetailDialog, useStereoIcon } from '../../hooks';
import { ImageViewDialog } from '../image-view-dialog';

export const ServiceDetailDialog = () => {
    const { isOpen, close, productId, setOpen } = useProductDetailDialog();

    const {
        data: product,
        isFetching,
        isFetched,
        error,
        refetch,
        isRefetching,
    } = useFetchProductById(productId as string);
    const { t, i18n } = useTranslation();

    const getStereoIcon = useStereoIcon();
    const isEnglish = i18n.language === 'en';

    const [fileUrl, setfileUrl] = useState('');

    const [imgViewerOpen, setImageViewerOpen] = useState(false);
    const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

    const viewMediaBtnTapped = (media: MediaView) => {
        if (!media) {
            return;
        }

        const isPdf = media.contentType?.includes('pdf');
        setfileUrl(media?.url || '');

        if (!isPdf) {
            setImageViewerOpen(true);
            return;
        }

        setPdfViewerOpen(true);
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }

        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }

        return getStereoIcon(catalog.stereo as string);
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={setOpen}>
                <DialogContent className="p-0 pb-[38px]" aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle className="text-base">{t('serviceDetail.title')}</DialogTitle>
                    </DialogHeader>

                    {/* Loading State */}
                    {(isFetching || isRefetching) && (
                        <div className="p-[18px] pb-0 space-y-6">
                            <div className="space-y-3">
                                <Skeleton className="h-5 w-32" />
                                <Skeleton className="h-4 w-48" />
                            </div>

                            <div className="flex gap-2">
                                <Skeleton className="w-10 h-10 rounded-full" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-5 w-40" />
                                    <Skeleton className="h-4 w-60" />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <Skeleton className="h-5 w-36" />
                                <div className="bg-box p-4 rounded-lg space-y-3">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-16 w-full" />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <Skeleton className="h-5 w-40" />
                                <div className="bg-box p-4 rounded-lg space-y-3">
                                    {[...Array(4)].map((_, i) => (
                                        <div key={i} className="flex justify-between">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-4 w-16" />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Skeleton className="h-5 w-44" />
                                <Skeleton className="h-10 w-full" />
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {error && !isFetching && (
                        <div className="flex flex-col items-center justify-center p-8 space-y-4">
                            <AlertCircle className="w-12 h-12 text-destructive" />
                            <div className="text-center">
                                <p className="text-sm font-medium text-destructive mb-1">
                                    {t('serviceDetail.error.loadFailed')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {error instanceof Error ? error.message : t('serviceDetail.error.unexpected')}
                                </p>
                            </div>
                            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
                                <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
                                {t('serviceDetail.error.tryAgain')}
                            </Button>
                        </div>
                    )}

                    {/* Success State */}
                    {product && !error && !(isFetching && isRefetching) && (
                        <div className="p-[18px] pb-0 overflow-auto">
                            <DialogDescription className="text-[18px] font-medium mb-[14px]">
                                {t('serviceDetail.requestedService')}
                            </DialogDescription>
                            <div className="text-[12px]  flex items-center justify-between mb-[10px]">
                                <div className="text-sub-title">
                                    {t('serviceDetail.applicationDate')} {formatDate(product.createdAt, 'YYYY. MM. DD')}
                                </div>
                            </div>
                            <div>
                                <div className="flex gap-2">
                                    <Avatar>
                                        <AvatarImage
                                            src={getIconImage(product.catalog$)}
                                            alt={product.catalog$?.stereo}
                                        />
                                    </Avatar>
                                    <div className="flex-1">
                                        <div className="text-[18px] font-medium">{product.name}</div>
                                        {(isEnglish ? product.catalog$?.summaryEn : product.catalog$?.summary) && (
                                            <div className="text-sm text-muted-foreground mt-1">
                                                {isEnglish ? product.catalog$.summaryEn : product.catalog$.summary}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Service Information Section */}
                            {product.catalog$ &&
                                ((isEnglish ? product.catalog$.descEn : product.catalog$.desc) ||
                                    (product.catalog$.dependency$$ && product.catalog$.dependency$$.length > 0)) && (
                                    <>
                                        <div className="my-6 border-b border-divider"></div>
                                        <div className="text-[18px] font-medium mb-3">
                                            {t('serviceDetail.serviceInfo')}
                                        </div>

                                        <Card className="bg-box py-[18px] px-[14px] rounded-lg">
                                            <div className="flex flex-col gap-6">
                                                {/* Description */}
                                                {(isEnglish ? product.catalog$.descEn : product.catalog$.desc) && (
                                                    <div>
                                                        <div className="text-foreground font-semibold text-base mb-3">
                                                            {t('serviceDetail.description')}
                                                        </div>
                                                        <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                                                            {isEnglish
                                                                ? product.catalog$.descEn
                                                                : product.catalog$.desc}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Dependencies */}
                                                {product.catalog$.dependency$$ &&
                                                    product.catalog$.dependency$$.length > 0 && (
                                                        <div>
                                                            <div className="text-foreground font-semibold text-base mb-4">
                                                                {t('serviceDetail.dependencies')}
                                                                <span className="text-point font-normal text-sm ml-2">
                                                                    ({product.catalog$.dependency$$.length})
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col gap-3">
                                                                {product.catalog$.dependency$$.map((dep, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="flex items-center justify-between p-3 bg-background border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                                                    >
                                                                        <div className="font-medium text-foreground">
                                                                            {dep.name}
                                                                        </div>
                                                                        <div className="text-xs font-medium text-primary-foreground capitalize bg-primary px-3 py-1 rounded-full">
                                                                            {dep.stereo}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        </Card>
                                    </>
                                )}

                            <div className="my-6 border-b border-divider"></div>
                            <div className="text-[18px] font-medium mb-3">{t('serviceDetail.paymentInfo')}</div>
                            <div className="flex flex-col gap-2">
                                <Card className="bg-box py-[18px] px-[14px] rounded-lg flex flex-col gap-[10px]">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sub-title">{t('price.service.initial_setup_fee')}</div>
                                        <div className="font-medium">
                                            {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                            {product.price$?.initPrice?.toLocaleString() || 0}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sub-title">{t('price.service.traffic_cost')}</div>
                                        <div className="font-medium">
                                            {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                            {product.price$?.trafficPrice?.toLocaleString() || 0}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sub-title">{t('price.service.cost_per_hour')}</div>
                                        <div className="font-medium">
                                            {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                            {product.price$?.hourPerPrice?.toLocaleString() || 0}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sub-title">{t('price.service.per_call')}</div>
                                        <div className="font-medium">
                                            {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                            {product.price$?.callPerPrice?.toLocaleString() || 0}
                                        </div>
                                    </div>
                                    {/* <div className="flex items-center justify-between text-[20px]">
                                        <div>Total Payment Amount</div>
                                        <div className="font-semibold">$nn</div>
                                    </div> */}
                                </Card>
                            </div>

                            <div className="my-6 border-b border-divider"></div>
                            <div className="text-[18px] font-medium mb-3">{t('serviceDetail.additionalInfo')}</div>
                            <div>
                                <Label variant="form">{t('serviceDetail.memo')}</Label>
                                <Input
                                    placeholder={t('serviceDetail.memoPlaceholder')}
                                    className="bg-box"
                                    disabled
                                    value={product.memo}
                                />
                            </div>
                            {product.media$$ && product.media$$.length > 0 && (
                                <>
                                    <div className="my-7 border-b border-divider"></div>
                                    <div>
                                        <Label className="p-0 text-base font-semibold">
                                            {t('serviceDetail.attachFile')}
                                        </Label>
                                        <div className="overflow-auto -mx-[18px]">
                                            <div className="flex items-center gap-x-2 overflow-auto px-[18px]">
                                                {product.media$$?.map(media =>
                                                    media.contentType?.includes('pdf') ? (
                                                        <PdfItem key={media.id} fileUrl={media.url} />
                                                    ) : (
                                                        <ImageItem key={media.id} fileUrl={media.url} />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Empty State */}
                    {!product && !isFetching && !error && isFetched && (
                        <div className="flex flex-col items-center justify-center p-8 space-y-2">
                            <p className="text-sm font-medium text-muted-foreground">
                                {t('serviceDetail.empty.title')}
                            </p>
                            <p className="text-xs text-muted-foreground">{t('serviceDetail.empty.description')}</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <ImageViewDialog url={fileUrl} open={imgViewerOpen} setOpen={setImageViewerOpen} />
            <PdfViewDialog url={fileUrl} open={pdfViewerOpen} setOpen={setPdfViewerOpen} />
        </>
    );
};
